/**
 * useOnboardingStatus — drives the first-login survey gate.
 *
 * A user "needs onboarding" when they registered, never started (or had imported)
 * an application, and haven't completed the survey yet. This deliberately also
 * catches accounts that registered BEFORE the survey existed (survey_completed_at
 * is null for everyone until they complete it) — so already-registered users are
 * prompted to qualify on their next login too.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface OnboardingStatus {
  surveyDone: boolean;
  hasApplication: boolean;
  userType: string | null;
  /** Set when an existing membership has been linked to this account (Phase 4). */
  linkedMemberApplicationId: string | null;
}

export function useOnboardingStatus() {
  const { user } = useAuth();
  return useQuery<OnboardingStatus>({
    queryKey: ["onboarding-status", user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("survey_completed_at, user_type, linked_member_application_id")
        .eq("id", user!.id)
        .maybeSingle();

      const { count } = await supabase
        .from("applications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user!.id);

      const p = profile as {
        survey_completed_at?: string | null;
        user_type?: string | null;
        linked_member_application_id?: string | null;
      } | null;
      return {
        surveyDone: !!p?.survey_completed_at,
        hasApplication: (count ?? 0) > 0,
        userType: p?.user_type ?? null,
        linkedMemberApplicationId: p?.linked_member_application_id ?? null,
      };
    },
  });
}
